import React from 'react';
import { Stars } from './Stars';
import type { IconSize } from '../icons';
import { cn } from '@shared/lib/utils';

export type RatingSize = 'sm' | 'md' | 'lg' | 'xl';

export interface RatingStarsProps {
  /**
   * Rating value (0-5)
   */
  rating: number;
  /**
   * Number of reviews
   */
  reviewCount: number;
  /**
   * Display variant
   * @default 'inline'
   */
  variant?: 'inline' | 'vertical';
  /**
   * Overall size preset
   * @default 'md'
   */
  size?: RatingSize;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Max rating (number of stars to display)
   * @default 5
   */
  maxRating?: number;
  /**
   * Custom star size (overrides size preset)
   */
  starSize?: IconSize;
  /**
   * Custom rating text size
   */
  ratingTextSize?: string;
  /**
   * Custom review count text size
   */
  reviewTextSize?: string;
  /**
   * Gap between stars
   * @default 'gap-0.5'
   */
  starGap?: 'gap-0' | 'gap-0.5' | 'gap-1' | 'gap-1.5' | 'gap-2';
  /**
   * Gap between rating elements (in inline mode)
   * @default 'gap-1.5'
   */
  elementGap?: string;
  /**
   * Gap between elements (in vertical mode)
   * @default 'gap-1'
   */
  verticalGap?: string;
  /**
   * Star color (from design system)
   * @default 'text-feedback-success'
   */
  starColor?: string;
  /**
   * Rating number color (from design system)
   * @default 'text-feedback-success'
   */
  ratingColor?: string;
  /**
   * Review count color (from design system)
   * @default 'text-secondary'
   */
  reviewColor?: string;
}

// Size presets
const sizePresets = {
  sm: {
    ratingText: 'text-sm',
    starSize: 'xs' as IconSize,
    reviewText: 'text-xs',
  },
  md: {
    ratingText: 'text-base',
    starSize: 'sm' as IconSize,
    reviewText: 'text-sm',
  },
  lg: {
    ratingText: 'text-xl',
    starSize: 'md' as IconSize,
    reviewText: 'text-base',
  },
  xl: {
    ratingText: 'text-3xl',
    starSize: 'lg' as IconSize,
    reviewText: 'text-lg',
  },
};

const verticalSizePresets = {
  sm: {
    ratingText: 'text-xl',
    starSize: 'sm' as IconSize,
    reviewText: 'text-xs',
  },
  md: {
    ratingText: 'text-[32px]',
    starSize: 'md' as IconSize,
    reviewText: 'text-sm',
  },
  lg: {
    ratingText: 'text-5xl',
    starSize: 'lg' as IconSize,
    reviewText: 'text-base',
  },
  xl: {
    ratingText: 'text-6xl',
    starSize: 'xl' as IconSize,
    reviewText: 'text-lg',
  },
};

/**
 * RatingStars component
 *
 * Displays rating with stars and review count in two variants:
 * - inline: horizontal layout (rating, stars, count in one row)
 * - vertical: vertical layout (rating, stars, count stacked)
 *
 * @example
 * ```tsx
 * // Inline variant with default size
 * <RatingStars rating={4.9} reviewCount={23} variant="inline" />
 *
 * // Vertical variant with large size
 * <RatingStars rating={4.9} reviewCount={23} variant="vertical" size="lg" />
 *
 * // Custom sizes and colors
 * <RatingStars
 *   rating={4.9}
 *   reviewCount={23}
 *   variant="inline"
 *   starSize="lg"
 *   starGap="gap-1"
 *   starColor="text-yellow-500"
 *   ratingColor="text-blue-600"
 * />
 * ```
 */
export const RatingStars: React.FC<RatingStarsProps> = ({
  rating,
  reviewCount,
  variant = 'inline',
  size = 'md',
  className,
  maxRating = 5,
  starSize,
  ratingTextSize,
  reviewTextSize,
  starGap = 'gap-0.5',
  elementGap = 'gap-1.5',
  verticalGap = 'gap-1',
  starColor = 'text-feedback-success',
  ratingColor = 'text-feedback-success',
  reviewColor = 'text-secondary',
}) => {
  // Normalize rating to be between 0 and maxRating
  const normalizedRating = Math.min(Math.max(0, rating), maxRating);

  // Get size preset based on variant
  const preset = variant === 'vertical' ? verticalSizePresets[size] : sizePresets[size];

  // Use custom sizes or fall back to preset
  const finalStarSize = starSize || preset.starSize;
  const finalRatingTextSize = ratingTextSize || preset.ratingText;
  const finalReviewTextSize = reviewTextSize || preset.reviewText;

  if (variant === 'vertical') {
    return (
      <div className={cn('flex flex-col items-center', verticalGap, className)}>
        {/* Rating number */}
        <div className={cn(finalRatingTextSize, 'font-bold leading-none', ratingColor)}>
          {normalizedRating.toFixed(1)}
        </div>

        {/* Stars */}
        <Stars
          count={maxRating}
          rating={normalizedRating}
          size={finalStarSize}
          gap={starGap}
          color={starColor}
        />

        {/* Review count */}
        <div className={cn(finalReviewTextSize, 'font-normal leading-tight', reviewColor)}>
          {reviewCount} {reviewCount === 1 ? 'review' : 'reviews'}
        </div>
      </div>
    );
  }

  // Inline variant (default)
  return (
    <div className={cn('flex items-center', elementGap, className)}>
      {/* Rating number */}
      <span className={cn(finalRatingTextSize, 'font-bold leading-none', ratingColor)}>
        {normalizedRating.toFixed(1)}
      </span>

      {/* Stars */}
      <Stars
        count={maxRating}
        rating={normalizedRating}
        size={finalStarSize}
        gap={starGap}
        color={starColor}
      />

      {/* Review count */}
      <span className={cn(finalReviewTextSize, 'font-normal leading-tight', reviewColor)}>
        ({reviewCount})
      </span>
    </div>
  );
};
